import React, { useState } from 'react';
import { MapPin, Clock, Send, Check, Coffee, Music, Baby } from 'lucide-react';
import { IMAGES, CHURCH as CHURCH_DEFAULTS } from '../mock/mock';
import { submitVisit } from '../lib/api';
import { useSettings } from '../hooks/useSettings';
import { useToast } from '../hooks/use-toast';
import Reveal from '../components/Reveal';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm text-[#3a4d54] mb-2 uppercase tracking-[0.12em]">{label}</span>
    {children}
  </label>
);

const inputCls = 'w-full bg-white border border-[#d8d2c6] px-4 py-3.5 outline-none focus:border-[#17565f] transition-colors';

const PlanVisit = () => {
  const settings = useSettings();
  const CHURCH = { ...CHURCH_DEFAULTS, ...settings };
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', visitDate: '', people: '1', message: '' });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitVisit(form);
      setSubmitted(true);
      toast({ title: 'We can’t wait to meet you!', description: 'Your visit details have been received. Someone from our team will reach out soon.' });
    } catch (_) {
      setSubmitted(true);
      toast({ title: 'We can’t wait to meet you!', description: 'Your visit details have been received.' });
    } finally {
      setLoading(false);
    }
  };

  const expect = [
    { icon: Music, title: 'Worship', text: 'Heartfelt, contemporary worship that welcomes you into God’s presence.' },
    { icon: Coffee, title: 'A Warm Welcome', text: 'Grab a coffee, meet friendly faces, and come exactly as you are.' },
    { icon: Baby, title: 'Hope Kids', text: 'A safe, fun environment for your children ages 0–12 during service.' },
  ];

  return (
    <div>
      <section className="relative h-[52vh] min-h-[420px] flex items-center justify-center overflow-hidden">
        <img src={IMAGES.prayingHands} alt="Plan your visit" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b2027]/80 via-[#0b2027]/60 to-[#0b2027]/85" />
        <div className="relative text-center px-6">
          <Reveal>
            <p className="text-[#c9a24b] uppercase tracking-[0.36em] text-xs mb-5">We’d Love to Meet You</p>
            <h1 className="font-serif-display italic text-white text-4xl md:text-6xl">Plan Your Visit</h1>
          </Reveal>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="bg-[#f6f2ea] py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-16">
            <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-4">What to Expect</p>
            <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-5xl">Your First Sunday</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {expect.map((x, i) => (
              <Reveal key={x.title} delay={i * 120} className="text-center bg-white p-10 shadow-sm hover:shadow-xl transition-shadow duration-500">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#17565f]/40 mb-6">
                  <x.icon className="h-6 w-6 text-[#17565f]" />
                </div>
                <h3 className="font-serif-display text-2xl text-[#0f2e38] mb-3">{x.title}</h3>
                <p className="text-[#5a6b71] leading-relaxed">{x.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="bg-[#0f2e38] p-10 h-full flex flex-col justify-center">
              <h3 className="font-serif-display text-white text-3xl mb-8">Service Time &amp; Location</h3>
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <Clock className="h-5 w-5 text-[#c9a24b] mt-1" />
                  <p className="text-white/85">{CHURCH.serviceTime}</p>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin className="h-5 w-5 text-[#c9a24b] mt-1" />
                  <p className="text-white/85">{CHURCH.address}<br />{CHURCH.city}</p>
                </div>
              </div>
              <div className="overflow-hidden border border-white/10">
                <iframe
                  title="map"
                  src="https://www.google.com/maps?q=4105%20Skyline%20Dr%2C%20Knoxville%2C%20TN%2037914&output=embed"
                  className="w-full h-64"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={140}>
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center bg-[#f6f2ea] p-12">
                <div className="h-20 w-20 rounded-full bg-[#17565f] flex items-center justify-center mb-6">
                  <Check className="h-10 w-10 text-white" />
                </div>
                <h3 className="font-serif-display text-[#0f2e38] text-3xl mb-4">Thank You!</h3>
                <p className="text-[#5a6b71] max-w-sm">We’ve received your details and can’t wait to welcome you this Sunday. Someone from our team will be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Name"><input required value={form.name} onChange={set('name')} className={inputCls} /></Field>
                  <Field label="Email"><input required type="email" value={form.email} onChange={set('email')} className={inputCls} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Phone"><input value={form.phone} onChange={set('phone')} className={inputCls} /></Field>
                  <Field label="Planned Visit Date"><input type="date" value={form.visitDate} onChange={set('visitDate')} className={inputCls} /></Field>
                </div>
                <Field label="How many are coming?">
                  <select value={form.people} onChange={set('people')} className={inputCls}>
                    {['1', '2', '3', '4', '5+'].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </Field>
                <Field label="Anything we should know?">
                  <textarea rows={4} value={form.message} onChange={set('message')} className={inputCls} placeholder="Questions, prayer requests, kids' ages..." />
                </Field>
                <button disabled={loading} className="btn-shine w-full bg-[#c9a24b] text-[#0b2027] hover:bg-[#d8b566] py-4 uppercase tracking-[0.2em] text-sm font-700 transition-colors inline-flex items-center justify-center gap-3">
                  {loading ? 'Sending…' : <>Plan My Visit <Send className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default PlanVisit;
